// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useMemo } from 'react';

import { useInternalI18n } from '../../../i18n/context';
import InternalLink from '../../../link/internal';
import InternalLiveRegion from '../../../live-region/internal';
import InternalStatusIndicator from '../../../status-indicator/internal';
import { BaseComponentProps } from '../../base-component';
import { fireNonCancelableEvent, NonCancelableEventHandler } from '../../events';

import styles from './styles.css.js';

interface ChartStatusContainerProps extends BaseComponentProps {
  statusType: 'loading' | 'finished' | 'error';

  empty?: React.ReactNode;
  noMatch?: React.ReactNode;

  loadingText?: string;
  errorText?: string;
  recoveryText?: string;

  onRecoveryClick?: NonCancelableEventHandler;

  // From `getChartStatus`
  isEmpty?: boolean;
  isNoMatch?: boolean;
  showChart?: boolean;
}

export function getChartStatus<T, U>({
  externalData,
  visibleData,
  statusType,
}: {
  externalData: ReadonlyArray<T>;
  visibleData: ReadonlyArray<U>;
  statusType: 'loading' | 'finished' | 'error';
}) {
  const isEmpty = !visibleData || visibleData.length === 0;
  const isNoMatch = isEmpty && visibleData.length !== externalData.length;
  const showChart = statusType === 'finished' && !isEmpty;
  return { isEmpty, isNoMatch, showChart };
}

export default function ChartStatusContainer({
  statusType,
  errorText,
  loadingText,
  recoveryText,
  noMatch,
  empty,
  onRecoveryClick,
  isNoMatch,
  isEmpty,
  showChart,
}: ChartStatusContainerProps) {
  const i18n = useInternalI18n('[charts]');

  const statusContainer = useMemo(() => {
    const handleRecoveryClick = (event: CustomEvent) => {
      event.preventDefault();
      fireNonCancelableEvent(onRecoveryClick);
    };
    if (statusType === 'error') {
      const renderedRecoveryText = i18n('recoveryText', recoveryText);
      return (
        <span>
          <InternalStatusIndicator type="error">{i18n('errorText', errorText)}</InternalStatusIndicator>{' '}
          {!!renderedRecoveryText && !!onRecoveryClick && (
            <InternalLink onFollow={handleRecoveryClick} variant="recovery">
              {renderedRecoveryText}
            </InternalLink>
          )}
        </span>
      );
    }

    if (statusType === 'loading') {
      return <InternalStatusIndicator type="loading">{i18n('loadingText', loadingText)}</InternalStatusIndicator>;
    }

    if (isNoMatch) {
      return <div className={styles.empty}>{noMatch}</div>;
    }

    if (isEmpty) {
      return <div className={styles.empty}>{empty}</div>;
    }
  }, [i18n, statusType, onRecoveryClick, isEmpty, isNoMatch, recoveryText, loadingText, errorText, empty, noMatch]);

  return (
    <div className={styles.root}>
      <InternalLiveRegion>{!showChart && statusContainer}</InternalLiveRegion>
    </div>
  );
}



// WEBPACK FOOTER //
// /local/src/internal/components/chart-status-container/index.tsx